import { useState } from 'react';
import clsx from 'clsx';

import { AutomationTestingProps } from '@/types';

import { Button } from '@@/buttons';
import { CopyButton } from '@@/buttons/CopyButton';

interface Props extends AutomationTestingProps {
  /** What the text is, e.g. "details", for the Show and Hide link */
  label: string;
  text: string;
  copyAriaLabel: string;
  /** Wraps long lines, rather than scrolling them so that columns stay aligned */
  wrapLines?: boolean;
  /** The text is still loading, for text fetched when it is shown */
  isLoading?: boolean;
  onOpenChange?(isOpen: boolean): void;
}

/** Raw text behind a Show/Hide link, with Copy once it is shown. */
export function ExpandableText({
  label,
  text,
  copyAriaLabel,
  wrapLines = false,
  isLoading = false,
  onOpenChange,
  'data-cy': dataCy,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Button
          color="link"
          size="xsmall"
          className="!ml-0 !p-0"
          onClick={() => toggle(!isOpen)}
          aria-expanded={isOpen}
          data-cy={`${dataCy}-toggle`}
        >
          {isOpen ? `Hide ${label}` : `Show ${label}`}
        </Button>
        {isOpen && !isLoading && (
          <CopyButton
            copyText={text}
            indicatorPosition="left"
            aria-label={copyAriaLabel}
            data-cy={`${dataCy}-copy`}
          >
            Copy
          </CopyButton>
        )}
      </div>
      {isOpen && (
        <pre
          className={clsx(
            'mt-1 max-h-80 overflow-auto',
            wrapLines
              ? 'break-words whitespace-pre-wrap'
              : 'w-0 min-w-full whitespace-pre'
          )}
        >
          {isLoading ? 'Loading...' : text}
        </pre>
      )}
    </div>
  );

  function toggle(open: boolean) {
    setIsOpen(open);
    onOpenChange?.(open);
  }
}
